import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './OmicsView.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faArrowLeft, 
    faSearch, 
    faFilter,
    faDna,
    faBacteria,
    faTooth,
    faCircleNodes,
    faAtom,
    faOilCan,
    faFlaskVial,
    faDroplet
} from '@fortawesome/free-solid-svg-icons';
import GenomicsReport from './reports/GenomicsReport';
import GutHealthReport from './reports/GutHealthReport';
import OralHealthReport from './reports/OralHealthReport';
import TranscriptomeReport from './reports/TranscriptomeReport';
import ProteomicsReport from './reports/ProteomicsReport';
import LipidomeReport from './reports/LipidomeReport';
import MetabolomicReport from './reports/MetabolomicReport';

const OmicsViewModal = ({ isOpen, onClose, omicsType, omicsId, omicsData }) => {
    const [selectedType, setSelectedType] = useState(omicsType || null);
    const [selectedId, setSelectedId] = useState(omicsId || null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({
        date: 'all',
        status: 'all'
    });
    const [showTypeSelection, setShowTypeSelection] = useState(true);
    const [showReport, setShowReport] = useState(false);
    const [samples, setSamples] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Animation states
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [contentKey, setContentKey] = useState(showReport ? 'report' : 'typeSelection'); // For content transition
    
    // Define kit types with icons
    const kitTypes = [
        { name: 'Whole Genome', icon: faDna, state: 'active' },
        { name: 'Gut Health', icon: faBacteria, state: 'disabled' },
        { name: 'Oral Health', icon: faTooth, state: 'disabled' },
        { name: 'Transcriptome', icon: faCircleNodes, state: 'disabled' },
        { name: 'Proteomics', icon: faAtom, state: 'disabled' },
        { name: 'Lipidome', icon: faOilCan, state: 'disabled' },
        { name: 'Metabolomic Urine', icon: faFlaskVial, state: 'disabled' },
        { name: 'Metabolomic Plasma', icon: faDroplet, state: 'disabled' }
    ];
    
    // Fetch samples based on selected type
    useEffect(() => {
        if (!selectedType) return;
        
        const fetchSamples = async () => {
            setLoading(true);
            try {
                const response = await fetch(`https://service1.szapfs.org/retrievesamples/${selectedType}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                setSamples(data);
                setError(null);
            } catch (err) {
                console.error('Error fetching samples:', err);
                setError('Failed to load samples. Please try again later.');
                setSamples([]);
            } finally {
                setLoading(false);
            }
        };
        
        fetchSamples();
    }, [selectedType]);
    
    // Initialization effect when omicsType and omicsId are provided
    useEffect(() => {
        if (omicsType && omicsId) {
            setSelectedType(omicsType);
            setSelectedId(omicsId);
            setShowTypeSelection(false);
            setShowReport(true);
            setContentKey('report');
            console.log('Opening report with type:', omicsType, 'and ID:', omicsId);
        }
    }, [omicsType, omicsId]);
    
    // Handle modal visibility animation
    useEffect(() => {
        if (isOpen) {
            setIsClosing(false);
            // Short timeout to ensure CSS transition happens
            setTimeout(() => {
                setIsVisible(true);
            }, 50);
        } else {
            setIsVisible(false);
        }
    }, [isOpen]);
    
    // Update content key when view changes
    useEffect(() => {
        let newContentKey = 'typeSelection';
        if (showReport) {
            newContentKey = 'report';
        } else if (!showTypeSelection) {
            newContentKey = 'idSelection';
        }
        
        if (contentKey !== newContentKey) {
            setContentKey(newContentKey);
        }
    }, [showTypeSelection, showReport, contentKey]);
    
    // Handle type selection
    const handleTypeSelect = (typeName) => {
        setSelectedType(typeName);
        setShowTypeSelection(false);
    };
    
    // Handle ID selection
    const handleIdSelect = (id) => {
        setSelectedId(id);
        setShowReport(true);
    };
    
    // Handle back button for report view
    const handleBackFromReport = () => {
        setShowReport(false);
    };
    
    // Handle back button for ID selection
    const handleBackClick = () => {
        setShowTypeSelection(true);
        setSelectedType(null);
    };
    
    // Custom close handler with animation
    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            onClose();
        }, 300); // Match this with CSS transition duration
    };
    
    // Handle search input
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };
    
    // Toggle filter section
    const toggleFilter = () => {
        setFilterOpen(!filterOpen);
    };
    
    // Handle filter changes
    const handleFilterChange = (filterName, value) => {
        setFilters({
            ...filters,
            [filterName]: value
        });
    };
    
    // Filter samples based on searchTerm
    const getFilteredSamples = () => {
        if (!samples || samples.length === 0) return [];
        
        let filtered = samples;
        
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(sample => 
                sample.id.toLowerCase().includes(searchLower)
            );
        }
        
        return filtered;
    };

    const renderTypeSelection = () => (
        <div className="OmicsView-type-grid">
            {kitTypes.map((type, index) => (
                <div 
                    key={index} 
                    className={`OmicsView-type-card ${type.state === 'disabled' ? 'disabled' : ''}`}
                    onClick={() => type.state !== 'disabled' && handleTypeSelect(type.name)}
                    style={{ opacity: type.state === 'disabled' ? 0.5 : 1, cursor: type.state === 'disabled' ? 'default' : 'pointer' }}
                >
                    <div className="OmicsView-type-icon">
                        <FontAwesomeIcon icon={type.icon} />
                    </div>
                    <div className="OmicsView-type-name">{type.name}</div>
                </div>
            ))}
        </div>
    );

    const renderIdSelection = () => {
        const filteredSamples = getFilteredSamples();
        
        return (
            <div className="OmicsView-content">
                <div className="OmicsView-search-filter">
                    <div className="OmicsView-search">
                        <input 
                            type="text" 
                            placeholder="Search reports..." 
                            value={searchTerm}
                            onChange={handleSearch}
                            className="OmicsView-search-input"
                        />
                    </div>
                    
                </div>
                
                <div className="OmicsView-id-grid">
                    {loading ? (
                        <div className="OmicsView-loading"> </div>
                    ) : error ? (
                        <div className="OmicsView-error">{error}</div>
                    ) : filteredSamples.length === 0 ? (
                        <div className="OmicsView-no-results">No reports found matching your criteria</div>
                    ) : (
                        filteredSamples.map((sample, index) => (
                            <div 
                                key={index} 
                                className="OmicsView-id-card"
                                onClick={() => handleIdSelect(sample.id)}
                            >
                                <div className="OmicsView-id-info">
                                    <div className="OmicsView-id-number">{sample.id}</div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };

    const renderReport = () => {
        switch (selectedType) {
            case 'Whole Genome':
                return <GenomicsReport id={selectedId} data={omicsData || { patientId: selectedId }} />;
            case 'Gut Health':
                return <GutHealthReport id={selectedId} data={omicsData || { patientId: selectedId }} />;
            case 'Oral Health':
                return <OralHealthReport id={selectedId} data={omicsData || { patientId: selectedId }} />;
            case 'Transcriptome':
                return <TranscriptomeReport id={selectedId} data={omicsData || { patientId: selectedId }} />;
            case 'Proteomics':
                return <ProteomicsReport id={selectedId} data={omicsData || { patientId: selectedId }} />;
            case 'Lipidome':
                return <LipidomeReport id={selectedId} data={omicsData || { patientId: selectedId }} />;
            case 'Metabolomic Urine':
                return <MetabolomicReport id={selectedId} data={omicsData || { patientId: selectedId }} reportType="urine" />;
            case 'Metabolomic Plasma':
                return <MetabolomicReport id={selectedId} data={omicsData || { patientId: selectedId }} reportType="plasma" />;
            default:
                return <div>No report available for this type</div>;
        }
    };
    
    // Return null if modal is closed
    if (!isOpen) return null;
    
    // Determine which content to display
    const renderContent = () => {
        if (showReport) {
            return renderReport();
        } else if (showTypeSelection) {
            return renderTypeSelection();
        } else {
            return renderIdSelection();
        }
    };
    
    // Use React Portal to render directly to document body
    return ReactDOM.createPortal(
        <div className={`OmicsView-overlay omicsviewfadein ${isVisible ? 'active' : ''}`}>
            <div className={`OmicsView-container omicsviewfadein ${isVisible ? 'active' : ''}`}>
                <div className="OmicsView-header" style={{ height: '5%' }}>
                    {!showTypeSelection && !showReport && (
                        <button className="OmicsView-back-button" onClick={handleBackClick}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>
                    )}
                    
                    {showReport && (
                        <button className="OmicsView-back-button" onClick={handleBackFromReport}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>
                    )}
                    
                    <h2 className="OmicsView-title">
                        {showTypeSelection ? "Select Omics Type" : 
                         showReport ? <div className="OmicsView-report-title"> {selectedType} Report <div className="OmicsView-report-id">({selectedId || 'No ID'})</div></div> : 
                         `${selectedType} Reports`}
                    </h2>
                
                    <button className="OmicsView-close-button" onClick={handleClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                <div className="OmicsView-content" style={{ height: '95%' }}>
                    <div className="omicsviewfadein active">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>,
        document.body // Render directly to document.body
    );
};

export default OmicsViewModal;